/**
 * Travel Desk — the desk's side of a trip: the booking queue, the booking actions, the desk's
 * documents, the audited traveller reveal, desk teams and vendors (ADR 0030, plan §5.6 second
 * bullet). Mounted under `/travel/desk`. The traveller's, approver's and HR's side is
 * `travel-request.contract.ts`; the policy admin is `travel-policy.contract.ts`. Declared on its own
 * (invariant 8) before any table, route or screen consumes it.
 *
 * THE UNIT OF WORK IS A BOOKING, not a trip. An approved trip fans out into one booking per leg and
 * per stay (`travel-request.contract.ts` will show the traveller their side of each). Each booking has a team,
 * an optional assignee and a stored `dueAt` (D19: the SLA, capped at the last working day before
 * departure), so the queue sorts and the per-tick sweep marks a breach once, from stored instants —
 * the helpdesk's pattern (D16), not its tickets.
 *
 * THE DESK'S LOOP (statuses: `TRAVEL_DESK_BOOKING_STATUSES`):
 *   1. `claimBooking` (manual teams) or the round-robin assigns it; `reassignBooking` moves it.
 *   2. Either `shareOptions` (the traveller picks with `selectOption`, a `travel-request.contract.ts` route that lands with the desk phase; an
 *      option that expires sends the trip to `needs_action`, EC-13) or straight to
 *   3. `confirmBooking` (vendor, reference, invoice and GST, the ticket uploaded with
 *      `uploadDocument`) — or `declineBooking` with a reason (EC-12). One PNR or invoice covering
 *      several travellers is `createSharedBooking`: it confirms every member with its own split
 *      (EC-15). A confirmation over the approved estimate by more than the policy's tolerance does
 *      not confirm: the server raises the `desk_overspend` amendment for the manager and the
 *      booking goes `on_hold` (D11, EC-14) — `pendingAmendmentId` says which.
 *   4. After approval of a change or cancel, `cancelBooking` records the charge and any refund;
 *      `recordRefund` closes the loop when the money or the credit note arrives (EC-18), and a
 *      refund that will never arrive is `forfeitRefund`. Credits (`listCredits`) are offered the
 *      next time the same traveller books with that vendor (EC-17).
 *
 * WHO MAY CALL WHAT (plan §5.5). `travel.desk:read` reads the queue, history and bookings of the
 * caller's own teams; `travel.desk:book` acts on them; `travel.desk:manage` is teams, members,
 * coverage, vendors, reassign, and every team's queue. The metadata is the entry gate; the
 * record-level rules live in the handlers and are pure (`desk-access.ts`): a booking of a team the
 * caller is not on is a 404, never a 403; nobody acts on their OWN trip, and the round-robin skips
 * them (EC-22). `scope: 'all'` on the queue and history is `travel.desk:manage` (a conditional
 * branch in the handler, as a route declared `travel:request` adds one for `travel:read`).
 * Neither desk role settles money, sets policy, overrides a decision or sees anyone's pay (D3).
 *
 * MONEY IS A DECIMAL STRING with the trip's currency beside it (invariant 2; trip money is in the
 * traveller's legal-entity currency, EC-6). Every booking write takes an Idempotency-Key
 * (invariant 10) and every write to a booking carries its `version` — a second agent confirming the
 * same booking gets a 409 `stale_version` and reads the first one's result (EC-54). What the desk
 * records is what finance pays out of band: suppliers are not paid in the HRMS (D15).
 *
 * PII. The only route that returns a passport or visa number is `revealTraveller`: it needs
 * `travel.desk:book`, an open trip of the caller's own team (EC-41), is rate limited (D34), audited
 * per call, and is `ai: 'hidden'`. Document bytes are `ai: 'hidden'` too. Nothing else on this
 * contract carries PII beyond names. The desk's internal notes and SLA fields never reach the
 * traveller's DTOs.
 *
 * MODULE SWITCH. With `TravelSettings.enabled` off (D33) every route answers 404 `module_disabled`.
 * A trial organisation's read-only mode denies every write here; reads still work (EC-48).
 */

import { Decimal } from 'decimal.js';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  TRAVEL_BOOKING_EVENT_KINDS,
  TRAVEL_BOOKING_PROVIDERS,
  TRAVEL_CHARGE_BORNE_BY,
  TRAVEL_DESK_ASSIGNMENT_MODES,
  TRAVEL_PAID_BY,
  TRAVEL_REFUND_DESTINATIONS,
  TRAVEL_REFUND_STATUSES,
  TRAVEL_VENDOR_TYPES,
} from '../enums.js';
import {
  currencySchema,
  dateOnlySchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  paginatedSchema,
  uuidSchema,
} from '../schemas/common.js';
import {
  travelAmountSchema,
  travelBookingKindSchema,
  travelDeskBookingStatusSchema,
  travelClockTimeSchema,
  travelDocumentKindSchema,
  travelFrequentFlyerSchema,
  travelLegSchema,
  travelPersonRefSchema,
  travelPositiveAmountSchema,
  travelStaySchema,
  travelTripSchema,
  travelVersionSchema,
  tripGroupMemberSchema,
} from '../travel/schemas.js';
import { routeMeta } from './meta.js';

const c = initContract();

/** The header every booking or money write declares (see `expense.contract.ts`). */
const idempotencyHeadersSchema = z.object({
  'idempotency-key': idempotencyKeySchema,
});

const optionalText = (max: number) => z.string().trim().max(max).nullable().optional();

/** A query-string boolean: `?breached=true`. (`z.coerce.boolean()` would read "false" as true.) */
const queryBooleanSchema = z
  .enum(['true', 'false'])
  .optional()
  .transform((value) => (value === undefined ? undefined : value === 'true'));

const bookingParams = z.object({ bookingId: uuidSchema });
const tripParams = z.object({ tripId: uuidSchema });
const documentParams = z.object({ documentId: uuidSchema });
const sharedBookingParams = z.object({ sharedBookingId: uuidSchema });
const teamParams = z.object({ teamId: uuidSchema });
const vendorParams = z.object({ vendorId: uuidSchema });

// ---------------------------------------------------------------------------
// Enum schemas that only the desk uses
// ---------------------------------------------------------------------------

export const travelDeskBookingEventKindSchema = z.enum(TRAVEL_BOOKING_EVENT_KINDS);
export const travelDeskBookingProviderSchema = z.enum(TRAVEL_BOOKING_PROVIDERS);
export const travelDeskPaidBySchema = z.enum(TRAVEL_PAID_BY);
export const travelDeskChargeBorneBySchema = z.enum(TRAVEL_CHARGE_BORNE_BY);
export const travelDeskRefundStatusSchema = z.enum(TRAVEL_REFUND_STATUSES);
export const travelDeskRefundDestinationSchema = z.enum(TRAVEL_REFUND_DESTINATIONS);
export const travelDeskAssignmentModeSchema = z.enum(TRAVEL_DESK_ASSIGNMENT_MODES);
export const travelDeskVendorTypeSchema = z.enum(TRAVEL_VENDOR_TYPES);

/** An airline credit's state, derived from its balance and expiry. */
export const TRAVEL_DESK_CREDIT_STATUSES = ['active', 'used', 'expired'] as const;
export const travelDeskCreditStatusSchema = z.enum(TRAVEL_DESK_CREDIT_STATUSES);

/** The queue's scopes: what is assigned to me, my teams' work, or every team's (manage). */
export const TRAVEL_DESK_QUEUE_SCOPES = ['mine', 'team', 'all'] as const;
export const travelDeskQueueScopeSchema = z.enum(TRAVEL_DESK_QUEUE_SCOPES);

/** What still needs the desk: the default of the queue when no `status` is asked for. */
export const TRAVEL_DESK_OPEN_BOOKING_STATUSES = ['pending', 'options_shared', 'on_hold'] as const;

/** What has left the queue, for history. */
export const TRAVEL_DESK_CLOSED_BOOKING_STATUSES = [
  'confirmed',
  'declined',
  'cancelled',
  'replaced',
] as const;

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------

/**
 * An Indian GSTIN: 2-digit state code, the holder's PAN, an entity number, `Z`, a check character.
 * Shape only — the route does not verify it against the GST network (D31).
 */
export const travelDeskGstinSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/, 'Not a valid GSTIN');

const bookingRefSchema = z.string().trim().min(1).max(60);

/** Tax is part of the amount payable, so it can never exceed it. */
const taxWithinAmount = (row: { amount: string; taxAmount?: string }): boolean =>
  row.taxAmount === undefined || new Decimal(row.taxAmount).lte(row.amount);
const TAX_ABOVE_AMOUNT = { message: 'The tax cannot exceed the amount', path: ['taxAmount'] };

// ---------------------------------------------------------------------------
// Vendors
// ---------------------------------------------------------------------------

export const travelDeskVendorSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  type: travelDeskVendorTypeSchema,
  gstin: travelDeskGstinSchema.nullable(),
  contactName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  notes: z.string().nullable(),
  /** An inactive vendor keeps its bookings but is not offered for new ones (EC-55). */
  isActive: z.boolean(),
});
export type TravelDeskVendor = z.infer<typeof travelDeskVendorSchema>;

const vendorFields = {
  name: z.string().trim().min(2).max(120),
  type: travelDeskVendorTypeSchema,
  gstin: travelDeskGstinSchema.nullable().optional(),
  contactName: optionalText(120),
  contactEmail: z.string().trim().email().max(320).nullable().optional(),
  contactPhone: optionalText(30),
  notes: optionalText(500),
};

export const createTravelDeskVendorBodySchema = z.object(vendorFields);
export const updateTravelDeskVendorBodySchema = z.object({
  ...vendorFields,
  name: vendorFields.name.optional(),
  type: vendorFields.type.optional(),
  /** Deactivating never touches existing bookings (EC-55). Nothing is deleted. */
  isActive: z.boolean().optional(),
});

export const listTravelDeskVendorsQuerySchema = z.object({
  type: travelDeskVendorTypeSchema.optional(),
  q: z.string().trim().min(1).max(120).optional(),
  includeInactive: queryBooleanSchema,
});

// ---------------------------------------------------------------------------
// Documents (the desk's own files, D18)
// ---------------------------------------------------------------------------

/** A file the desk attached to a trip. Bytes: `downloadDocument`. */
export const travelDeskDocumentSchema = z.object({
  id: uuidSchema,
  tripId: uuidSchema,
  bookingId: uuidSchema.nullable(),
  kind: travelDocumentKindSchema,
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  uploadedById: uuidSchema,
  uploadedByName: z.string(),
  uploadedAt: instantSchema,
});
export type TravelDeskDocument = z.infer<typeof travelDeskDocumentSchema>;

/**
 * The upload body: base64 rather than multipart, like a claim receipt (`expense.contract.ts`), so
 * the route is `ai: 'hidden'` — a model cannot emit a file. The route allows PDF, JPEG and PNG up
 * to 5 MB (`storage.ts`) and is rate limited (D34); a client can claim any type, the route decides.
 */
export const uploadTravelDeskDocumentBodySchema = z.object({
  /** The booking the file belongs to; null for one about the whole trip (an itinerary). */
  bookingId: uuidSchema.nullable().optional(),
  kind: travelDocumentKindSchema,
  fileName: z.string().trim().min(1).max(255),
  mimeType: z.string().trim().min(1).max(100),
  /** The file itself, base64-encoded, no data: URI prefix. */
  contentBase64: z.string().min(1),
});

export const travelDeskDocumentDownloadSchema = z.object({
  fileName: z.string(),
  mimeType: z.string(),
  contentBase64: z.string(),
});

// ---------------------------------------------------------------------------
// Bookings — the queue row, the detail and the trip view
// ---------------------------------------------------------------------------

/**
 * One booking in a list. Everything the queue sorts, filters and greys on: the trip's own status
 * (a co-traveller whose trip was rejected is greyed in the group view, EC-11), who has it, and the
 * stored due instant. `summary` is a one-line description the server composes ("Delhi to Mumbai,
 * 12 Oct, economy" / "Mumbai, 2 nights") — no amounts, no PII.
 */
export const travelDeskBookingSchema = z.object({
  id: uuidSchema,
  tripId: uuidSchema,
  tripReference: z.string(),
  tripStatus: travelTripSchema.shape.status,
  kind: travelBookingKindSchema,
  status: travelDeskBookingStatusSchema,
  legId: uuidSchema.nullable(),
  stayId: uuidSchema.nullable(),
  summary: z.string(),
  personId: uuidSchema,
  personName: z.string(),
  /** Links co-travellers' trips (D5); the queue can filter to one group. */
  groupId: uuidSchema.nullable(),
  /** How many trips the group has, this one included; 1 when not grouped. */
  groupSize: z.number().int().positive(),
  /** The first day this booking is needed: the leg's date or the stay's check-in. */
  needByDate: dateOnlySchema,
  teamId: uuidSchema,
  teamName: z.string(),
  assignee: travelPersonRefSchema.nullable(),
  /** The stored deadline (D19); the queue sorts on it and the sweep reads it. */
  dueAt: instantSchema,
  /** Set once by the sweep (EC-45); null while on time. */
  breachedAt: instantSchema.nullable(),
  /** The deadline was already past when the booking was created: the trip leaves too soon (EC-56). */
  urgent: z.boolean(),
  vendorId: uuidSchema.nullable(),
  vendorName: z.string().nullable(),
  bookingRef: z.string().nullable(),
  /** The trip's approved estimate for this item — what the D11 tolerance is measured against. */
  estimatedAmount: travelAmountSchema.nullable(),
  /** The confirmed amount, tax included; null until the desk confirms. */
  amount: travelAmountSchema.nullable(),
  currency: currencySchema,
  /** The traveller's choice among shared options, while `options_shared`. */
  selectedOptionId: uuidSchema.nullable(),
  /** The earliest expiry of the options still on offer (EC-13). */
  optionsExpireAt: instantSchema.nullable(),
  /** The `desk_overspend` amendment holding this booking `on_hold`, if any (D11, EC-20). */
  pendingAmendmentId: uuidSchema.nullable(),
  version: travelVersionSchema,
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type TravelDeskBooking = z.infer<typeof travelDeskBookingSchema>;

/** One option the desk shared, with what the traveller was and was not shown. */
export const travelDeskBookingOptionSchema = z.object({
  id: uuidSchema,
  summary: z.string(),
  amount: travelAmountSchema,
  currency: currencySchema,
  vendorId: uuidSchema.nullable(),
  vendorName: z.string().nullable(),
  /** Computed by the server against the trip's policy snapshot; the desk does not set it. */
  withinPolicy: z.boolean(),
  expiresAt: instantSchema,
  selectedAt: instantSchema.nullable(),
  createdAt: instantSchema,
});
export type TravelDeskBookingOption = z.infer<typeof travelDeskBookingOptionSchema>;

/** One row of a booking's activity trail — desk-internal, never shown to the traveller. */
export const travelDeskBookingEventSchema = z.object({
  id: uuidSchema,
  kind: travelDeskBookingEventKindSchema,
  at: instantSchema,
  /** Null for something the system did (routing, the sweep, a hold). */
  actorPersonId: uuidSchema.nullable(),
  actorName: z.string().nullable(),
  /** A short sentence; carries no amounts beyond the money fields of the booking itself. */
  summary: z.string(),
  note: z.string().nullable(),
});
export type TravelDeskBookingEvent = z.infer<typeof travelDeskBookingEventSchema>;

/** The group PNR / invoice a booking is one traveller's share of (EC-15). */
export const travelDeskSharedBookingRefSchema = z.object({
  id: uuidSchema,
  bookingRef: z.string(),
  memberCount: z.number().int().positive(),
});

/** One booking, everything a desk agent needs to act on it. */
export const travelDeskBookingDetailSchema = travelDeskBookingSchema.extend({
  provider: travelDeskBookingProviderSchema,
  /** What to book: exactly one of these is present, matching `legId` / `stayId`. */
  leg: travelLegSchema.nullable(),
  stay: travelStaySchema.nullable(),
  options: z.array(travelDeskBookingOptionSchema),
  taxAmount: travelAmountSchema.nullable(),
  invoiceNumber: z.string().nullable(),
  /**
   * The vendor invoice's date (`YYYY-MM-DD`), or null when none was recorded. Optional on the wire
   * until every detail builder maps it (lib/travel/desk/booking-dto.ts, desk-teams/booking-detail.ts).
   */
  invoiceDate: dateOnlySchema.nullable().optional(),
  vendorGstin: z.string().nullable(),
  companyGstin: z.string().nullable(),
  paidBy: travelDeskPaidBySchema.nullable(),
  bookedAt: instantSchema.nullable(),
  bookedBy: travelPersonRefSchema.nullable(),
  declineReason: z.string().nullable(),
  cancelledAt: instantSchema.nullable(),
  cancellationCharge: travelAmountSchema.nullable(),
  chargeBorneBy: travelDeskChargeBorneBySchema.nullable(),
  /** The note a desk override of the default bearer needs (D27); null when the default applied. */
  chargeOverrideNote: z.string().nullable(),
  noShow: z.boolean(),
  refundAmount: travelAmountSchema.nullable(),
  refundStatus: travelDeskRefundStatusSchema,
  refundReceivedAt: instantSchema.nullable(),
  refundTo: travelDeskRefundDestinationSchema.nullable(),
  /** The airline credit this booking used, or the one its refund produced (EC-17). */
  creditId: uuidSchema.nullable(),
  replacesBookingId: uuidSchema.nullable(),
  replacedByBookingId: uuidSchema.nullable(),
  sharedBooking: travelDeskSharedBookingRefSchema.nullable(),
  documents: z.array(travelDeskDocumentSchema),
  /** Oldest first. */
  events: z.array(travelDeskBookingEventSchema),
});
export type TravelDeskBookingDetail = z.infer<typeof travelDeskBookingDetailSchema>;

/**
 * A trip as the desk sees it: the itinerary and dates, the co-travellers with their statuses, and
 * every booking the trip has. No policy result, cost split, advance or personal days — the desk
 * books; it does not judge, allocate or pay (D3).
 */
export const travelDeskTripSchema = travelTripSchema.extend({
  /** The zone the trip's dates are read in (EC-50). */
  timezone: z.string(),
  departureTime: travelClockTimeSchema.nullable(),
  returnTime: travelClockTimeSchema.nullable(),
  legs: z.array(travelLegSchema),
  stays: z.array(travelStaySchema),
  /** Co-travellers (D5): a member whose trip was rejected is greyed and their items skipped (EC-11). */
  groupMembers: z.array(tripGroupMemberSchema),
  bookings: z.array(travelDeskBookingSchema),
  documents: z.array(travelDeskDocumentSchema),
});
export type TravelDeskTrip = z.infer<typeof travelDeskTripSchema>;

// ---------------------------------------------------------------------------
// Queue, history and summary
// ---------------------------------------------------------------------------

const queueSortSchema = z.enum(['due', 'departure', 'created']);

/**
 * The desk queue. With no `status` it lists what still needs the desk (`pending`, `options_shared`,
 * `on_hold`). `scope` `team` (the default) is the caller's teams; `mine` is what is assigned to the
 * caller; `all` is every team and needs `travel.desk:manage`. `unassigned=true` is the team pool a
 * manual-mode agent claims from. Sorted by `sort`, then id, so the cursor is stable.
 */
export const listTravelDeskQueueQuerySchema = z.object({
  scope: travelDeskQueueScopeSchema.default('team'),
  teamId: uuidSchema.optional(),
  assigneePersonId: uuidSchema.optional(),
  unassigned: queryBooleanSchema,
  breached: queryBooleanSchema,
  status: travelDeskBookingStatusSchema.optional(),
  kind: travelBookingKindSchema.optional(),
  groupId: uuidSchema.optional(),
  /** Matches the trip reference and the traveller's name. */
  q: z.string().trim().min(1).max(120).optional(),
  sort: queueSortSchema.default('due'),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

/**
 * Bookings that have left the queue: `confirmed`, `declined`, `cancelled` and `replaced`. `from` /
 * `to` bound the day the booking last changed (in the org's desk zone).
 */
export const listTravelDeskHistoryQuerySchema = z.object({
  scope: travelDeskQueueScopeSchema.default('team'),
  teamId: uuidSchema.optional(),
  assigneePersonId: uuidSchema.optional(),
  status: z.enum(TRAVEL_DESK_CLOSED_BOOKING_STATUSES).optional(),
  kind: travelBookingKindSchema.optional(),
  vendorId: uuidSchema.optional(),
  refundStatus: travelDeskRefundStatusSchema.optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
  q: z.string().trim().min(1).max(120).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

export const travelDeskQueueSummaryQuerySchema = z.object({
  scope: travelDeskQueueScopeSchema.default('team'),
  teamId: uuidSchema.optional(),
});

/** The counts behind the queue's tabs and the SLA strip. All are of open bookings in scope. */
export const travelDeskQueueSummarySchema = z.object({
  open: z.number().int().nonnegative(),
  unassigned: z.number().int().nonnegative(),
  assignedToMe: z.number().int().nonnegative(),
  optionsShared: z.number().int().nonnegative(),
  onHold: z.number().int().nonnegative(),
  breached: z.number().int().nonnegative(),
  /** Due within the next 24 hours and not yet breached. */
  dueSoon: z.number().int().nonnegative(),
  /** Bookings on trips that need attention: the departure is inside the SLA (EC-56). */
  urgent: z.number().int().nonnegative(),
});
export type TravelDeskQueueSummary = z.infer<typeof travelDeskQueueSummarySchema>;

// ---------------------------------------------------------------------------
// SLA dashboard (desk manager)
// ---------------------------------------------------------------------------

export const travelDeskSlaQuerySchema = z.object({
  teamId: uuidSchema.optional(),
});

const slaCountsShape = {
  open: z.number().int().nonnegative(),
  unassigned: z.number().int().nonnegative(),
  breached: z.number().int().nonnegative(),
  /** Due within the next 24 hours and not yet breached. */
  dueSoon: z.number().int().nonnegative(),
  urgent: z.number().int().nonnegative(),
};

/** One agent's open workload inside a team. */
export const travelDeskSlaAgentRowSchema = z.object({
  person: travelPersonRefSchema,
  open: z.number().int().nonnegative(),
  breached: z.number().int().nonnegative(),
  dueSoon: z.number().int().nonnegative(),
});

/** One team's live position. `oldestDueAt` is the earliest stored deadline among its open bookings. */
export const travelDeskSlaTeamRowSchema = z.object({
  teamId: uuidSchema,
  teamName: z.string(),
  assignmentMode: travelDeskAssignmentModeSchema,
  isFallback: z.boolean(),
  ...slaCountsShape,
  oldestDueAt: instantSchema.nullable(),
  agents: z.array(travelDeskSlaAgentRowSchema),
});

/**
 * The desk manager's live SLA view: counts per team and per agent, from the stored `dueAt` and
 * `breachedAt` instants, so it never disagrees with the queue or the breach notification.
 * `uncoveredTripCount` is approved trips no team covers while no fallback team exists (EC-21):
 * nobody will book them until a manager assigns or fixes coverage. Live counts only; turnaround
 * over time is `getDeskPerformance` in the reports.
 */
export const travelDeskSlaDashboardSchema = z.object({
  asOf: instantSchema,
  totals: z.object(slaCountsShape),
  uncoveredTripCount: z.number().int().nonnegative(),
  teams: z.array(travelDeskSlaTeamRowSchema),
});
export type TravelDeskSlaDashboard = z.infer<typeof travelDeskSlaDashboardSchema>;

// ---------------------------------------------------------------------------
// Booking action bodies
// ---------------------------------------------------------------------------

/** Take an unassigned booking (`assignmentMode: 'manual'`). The route refuses a taken one with 409. */
export const claimTravelDeskBookingBodySchema = z.object({
  version: travelVersionSchema,
});

/**
 * Move a booking to another agent, back to the team pool (`assigneePersonId: null`), or to another
 * team (`teamId`, which resets the deadline). The new assignee must be an active member of the
 * team the booking ends up with and not the traveller (EC-22). Needs `travel.desk:manage`.
 */
export const reassignTravelDeskBookingBodySchema = z
  .object({
    version: travelVersionSchema,
    assigneePersonId: uuidSchema.nullable().optional(),
    teamId: uuidSchema.optional(),
    reason: optionalText(500),
  })
  .refine((body) => body.assigneePersonId !== undefined || body.teamId !== undefined, {
    message: 'Give an assignee, null for the team pool, or a team',
    path: ['assigneePersonId'],
  });

/**
 * One option to put in front of the traveller. The policy verdict is the server's, not the desk's
 * (`withinPolicy` on the stored option). `expiresAt` must be in the future — the route checks it
 * (422); the sweep reminds the traveller a day before and expires it (EC-13).
 */
export const travelDeskOptionInputSchema = z.object({
  summary: z.string().trim().min(1).max(500),
  amount: travelAmountSchema,
  vendorId: uuidSchema.nullable().optional(),
  expiresAt: instantSchema,
});

/** Share options. Sharing again replaces the options still on offer. */
export const shareTravelDeskOptionsBodySchema = z.object({
  version: travelVersionSchema,
  options: z.array(travelDeskOptionInputSchema).min(1).max(8),
});

const bookingMoneyFields = {
  vendorId: uuidSchema.nullable().optional(),
  invoiceNumber: optionalText(60),
  /** Defaults from the vendor's GSTIN when left out (D31). */
  vendorGstin: travelDeskGstinSchema.nullable().optional(),
  /** Defaults from the traveller's legal entity's registration when left out (D31). */
  companyGstin: travelDeskGstinSchema.nullable().optional(),
  /**
   * The vendor invoice's date (a calendar day, for GST input credit and the travel reports). The
   * route refuses a day after today in the desk's timezone (422). Left out: not recorded.
   */
  invoiceDate: dateOnlySchema.nullable().optional(),
};

/**
 * Confirm a booking. `amount` is what was paid, tax included, in the trip's currency; `taxAmount`
 * is the GST part of it. `optionId` records which shared option this fulfils (the traveller's
 * selection, EC-54 — the route refuses one that is not the current choice). `creditId` applies a
 * traveller's airline credit (EC-17); the route uses at most its balance. Over the approved
 * estimate by more than `bookingTolerancePct`, the booking is NOT confirmed: it goes `on_hold`
 * with a `desk_overspend` amendment for the manager (D11). An inactive vendor is a 422 (EC-55).
 */
export const confirmTravelDeskBookingBodySchema = z
  .object({
    version: travelVersionSchema,
    optionId: uuidSchema.nullable().optional(),
    bookingRef: bookingRefSchema,
    amount: travelAmountSchema,
    taxAmount: travelAmountSchema.default('0'),
    paidBy: travelDeskPaidBySchema.default('company'),
    creditId: uuidSchema.nullable().optional(),
    note: optionalText(500),
    ...bookingMoneyFields,
  })
  .refine(taxWithinAmount, TAX_ABOVE_AMOUNT);

/** The desk cannot book it: the trip goes `needs_action` and the traveller amends, self-books or drops (EC-12). */
export const declineTravelDeskBookingBodySchema = z.object({
  version: travelVersionSchema,
  reason: z.string().trim().min(1).max(500),
});

/**
 * Cancel a booking whose change or cancellation was approved (the route refuses one whose trip
 * has neither with 409). Who bears `cancellationCharge` defaults by the trip's cancel reason
 * (D27: business to the company, personal to the employee, recovered on the D4 ladder).
 * `chargeBorneBy` is sent ONLY to override that default, and an override needs a note; it is
 * audited and shown on the trip (EC-19). A `refundAmount` above zero starts a `pending` refund the
 * sweep chases weekly (EC-18). `noShow` marks a traveller who did not turn up.
 */
export const cancelTravelDeskBookingBodySchema = z
  .object({
    version: travelVersionSchema,
    cancellationCharge: travelAmountSchema.nullable().optional(),
    chargeBorneBy: travelDeskChargeBorneBySchema.optional(),
    chargeOverrideNote: z.string().trim().min(1).max(500).optional(),
    refundAmount: travelAmountSchema.nullable().optional(),
    noShow: z.boolean().default(false),
    note: optionalText(500),
  })
  .refine((body) => body.chargeBorneBy === undefined || body.chargeOverrideNote !== undefined, {
    message: 'Overriding who bears the charge needs a note',
    path: ['chargeOverrideNote'],
  });

/** The credit note a refund turned into, when `refundTo` is `credit_note`. */
export const travelDeskCreditInputSchema = z.object({
  /** The airline's credit-note number. */
  reference: z.string().trim().min(1).max(60),
  /** Null: the credit does not expire. */
  expiresOn: dateOnlySchema.nullable().optional(),
});

/**
 * The refund arrived. `credit_note` needs `credit` (and only it takes one): the amount becomes a
 * credit for the traveller with that vendor (EC-17). The route refuses an amount above what the
 * booking paid with 422.
 */
export const recordTravelDeskRefundBodySchema = z
  .object({
    version: travelVersionSchema,
    refundAmount: travelPositiveAmountSchema,
    /** The day the money or the credit note arrived, in the org's desk zone. */
    receivedOn: dateOnlySchema,
    refundTo: travelDeskRefundDestinationSchema,
    credit: travelDeskCreditInputSchema.optional(),
    note: optionalText(500),
  })
  .refine((body) => (body.refundTo === 'credit_note') === (body.credit !== undefined), {
    message: 'A credit note needs its details; the other destinations take none',
    path: ['credit'],
  });

/** Give up on a refund that will not come (EC-18). Writing off money is a manager's call. */
export const forfeitTravelDeskRefundBodySchema = z.object({
  version: travelVersionSchema,
  reason: z.string().trim().min(1).max(500),
});

/** An internal note on a booking, kept on its trail; the traveller never sees it. */
export const addTravelDeskBookingCommentBodySchema = z.object({
  body: z.string().trim().min(1).max(1000),
});

// ---------------------------------------------------------------------------
// Shared bookings (one PNR or invoice, several travellers — EC-15, EC-16)
// ---------------------------------------------------------------------------

/** One traveller's share of a group booking. `version` is that booking's token. */
export const travelDeskSharedSplitInputSchema = z
  .object({
    bookingId: uuidSchema,
    version: travelVersionSchema,
    amount: travelAmountSchema,
    taxAmount: travelAmountSchema.default('0'),
  })
  .refine(taxWithinAmount, TAX_ABOVE_AMOUNT);

const sumOf = (rows: readonly string[]): Decimal =>
  rows.reduce((total, value) => total.plus(value), new Decimal(0));

/**
 * Confirm several travellers' bookings under one reference. The splits must add up to the invoice
 * exactly (amount and tax), each booking appears once, and they must all be of one kind and one
 * legal entity (one company GSTIN) — the route checks the last two (422). The whole call succeeds
 * or nothing changes. Each member is then its own booking: one member leaving cancels only their
 * share and refund (EC-16). The tolerance rule of `confirmBooking` applies to each member.
 */
export const createTravelDeskSharedBookingBodySchema = z
  .object({
    kind: travelBookingKindSchema,
    /** The group PNR or confirmation number. */
    bookingRef: bookingRefSchema,
    totalAmount: travelAmountSchema,
    totalTaxAmount: travelAmountSchema.default('0'),
    paidBy: travelDeskPaidBySchema.default('company'),
    note: optionalText(500),
    splits: z.array(travelDeskSharedSplitInputSchema).min(2).max(20),
    ...bookingMoneyFields,
  })
  .superRefine((body, ctx) => {
    if (new Set(body.splits.map((s) => s.bookingId)).size !== body.splits.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['splits'],
        message: 'A booking can appear once',
      });
    }
    if (!sumOf(body.splits.map((s) => s.amount)).equals(body.totalAmount)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['totalAmount'],
        message: 'The splits must add up to the total amount',
      });
    }
    if (!sumOf(body.splits.map((s) => s.taxAmount)).equals(body.totalTaxAmount)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['totalTaxAmount'],
        message: 'The tax splits must add up to the total tax',
      });
    }
    if (new Decimal(body.totalTaxAmount).gt(body.totalAmount)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['totalTaxAmount'],
        message: 'The tax cannot exceed the amount',
      });
    }
  });

export const travelDeskSharedBookingSchema = z.object({
  id: uuidSchema,
  kind: travelBookingKindSchema,
  bookingRef: z.string(),
  vendorId: uuidSchema.nullable(),
  vendorName: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
  vendorGstin: z.string().nullable(),
  companyGstin: z.string().nullable(),
  totalAmount: travelAmountSchema,
  totalTaxAmount: travelAmountSchema,
  currency: currencySchema,
  createdAt: instantSchema,
  splits: z.array(
    z.object({
      bookingId: uuidSchema,
      tripId: uuidSchema,
      tripReference: z.string(),
      personId: uuidSchema,
      personName: z.string(),
      status: travelDeskBookingStatusSchema,
      amount: travelAmountSchema,
      taxAmount: travelAmountSchema,
    }),
  ),
});
export type TravelDeskSharedBooking = z.infer<typeof travelDeskSharedBookingSchema>;

// ---------------------------------------------------------------------------
// Credits (EC-17)
// ---------------------------------------------------------------------------

/** An airline credit a traveller holds with a vendor, offered on their next booking with it. */
export const travelDeskCreditSchema = z.object({
  id: uuidSchema,
  personId: uuidSchema,
  personName: z.string(),
  vendorId: uuidSchema.nullable(),
  vendorName: z.string().nullable(),
  /** The airline's credit-note number. */
  reference: z.string(),
  amount: travelAmountSchema,
  usedAmount: travelAmountSchema,
  /** `amount` less `usedAmount`. */
  balance: travelAmountSchema,
  currency: currencySchema,
  expiresOn: dateOnlySchema.nullable(),
  status: travelDeskCreditStatusSchema,
  /** The cancelled booking whose refund produced it. */
  sourceBookingId: uuidSchema.nullable(),
  createdAt: instantSchema,
});
export type TravelDeskCredit = z.infer<typeof travelDeskCreditSchema>;

/** `personId` + `vendorId` is the "credits I could offer for this booking" lookup. */
export const listTravelDeskCreditsQuerySchema = z.object({
  personId: uuidSchema.optional(),
  vendorId: uuidSchema.optional(),
  status: travelDeskCreditStatusSchema.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

// ---------------------------------------------------------------------------
// Traveller reveal (PII — audited, rate limited, ai hidden)
// ---------------------------------------------------------------------------

/**
 * Why the desk needs the passport: the booking it is for, which is what makes the audit row
 * meaningful and what the route checks the caller's team against. Refused (403) when the booking
 * is not one of the caller's own team's or is the caller's own trip, and (409) once the trip is no
 * longer open — the desk may see a passport only while there is something to book (EC-41).
 */
export const revealTravelDeskTravellerBodySchema = z.object({
  bookingId: uuidSchema,
});

/**
 * THE ONLY RESPONSE ON THE PLATFORM THAT CARRIES A FULL PASSPORT OR VISA NUMBER. Decrypted with
 * the person's DEK for this call, never logged, never cached; the call itself is the audit row.
 */
export const travelDeskTravellerRevealSchema = z.object({
  personId: uuidSchema,
  personName: z.string(),
  /** The name exactly as on the ID, which the booking must match. */
  nameAsOnId: z.string().nullable(),
  passportNumber: z.string().nullable(),
  passportCountry: z.string().nullable(),
  passportExpiry: dateOnlySchema.nullable(),
  visas: z.array(
    z.object({
      country: z.string(),
      visaType: z.string(),
      number: z.string().nullable(),
      validFrom: dateOnlySchema,
      validTo: dateOnlySchema,
      entries: z.string(),
    }),
  ),
  /** Free text, as the traveller saved it in their profile (`travellerProfileViewSchema`). */
  mealPreference: z.string().nullable(),
  seatPreference: z.string().nullable(),
  /** An empty list when the traveller saved none. */
  frequentFlyer: z.array(travelFrequentFlyerSchema),
});
export type TravelDeskTravellerReveal = z.infer<typeof travelDeskTravellerRevealSchema>;

// ---------------------------------------------------------------------------
// Teams, members, coverage (D16, D30)
// ---------------------------------------------------------------------------

/** What a team can cover, most specific first (D30). */
export const TRAVEL_DESK_COVERAGE_KINDS = ['location', 'business_unit', 'legal_entity'] as const;
export const travelDeskCoverageKindSchema = z.enum(TRAVEL_DESK_COVERAGE_KINDS);

export const travelDeskCoverageInputSchema = z.object({
  kind: travelDeskCoverageKindSchema,
  id: uuidSchema,
});

export const travelDeskCoverageSchema = travelDeskCoverageInputSchema.extend({
  /** The location, business unit or legal entity's name. */
  name: z.string(),
});
export type TravelDeskCoverage = z.infer<typeof travelDeskCoverageSchema>;

export const travelDeskTeamSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  assignmentMode: travelDeskAssignmentModeSchema,
  /** Notified of SLA breaches with the audience the org configured. Must be a member. */
  lead: travelPersonRefSchema.nullable(),
  members: z.array(travelPersonRefSchema),
  coverage: z.array(travelDeskCoverageSchema),
  /** The org's fallback team takes trips no coverage row matches (EC-21). */
  isFallback: z.boolean(),
  isActive: z.boolean(),
  /** Open bookings the team holds, for the manager's workload view. */
  openBookingCount: z.number().int().nonnegative(),
});
export type TravelDeskTeam = z.infer<typeof travelDeskTeamSchema>;

/** Coverage rows are unique by (kind, id); a team may cover several places. */
const coverageSetSchema = z
  .array(travelDeskCoverageInputSchema)
  .max(200)
  .refine((rows) => new Set(rows.map((r) => `${r.kind}:${r.id}`)).size === rows.length, {
    message: 'A place can be covered once per team',
  });

export const createTravelDeskTeamBodySchema = z.object({
  name: z.string().trim().min(2).max(80),
  description: optionalText(500),
  assignmentMode: travelDeskAssignmentModeSchema.default('round_robin'),
  memberPersonIds: z.array(uuidSchema).max(200).default([]),
  leadPersonId: uuidSchema.nullable().optional(),
  coverage: coverageSetSchema.default([]),
});

/**
 * Change a team. `memberPersonIds` and `coverage` REPLACE the sets (helpdesk style); adding or
 * removing a member changes their implicit `travel_desk_agent` role at once (TD-3.16), and a
 * removed member's open bookings go back to the team pool (EC-44). Deactivating the org's
 * fallback team is a 409 until another is chosen.
 */
export const updateTravelDeskTeamBodySchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  description: optionalText(500),
  assignmentMode: travelDeskAssignmentModeSchema.optional(),
  memberPersonIds: z.array(uuidSchema).max(200).optional(),
  leadPersonId: uuidSchema.nullable().optional(),
  coverage: coverageSetSchema.optional(),
  isActive: z.boolean().optional(),
});

export const listTravelDeskTeamsQuerySchema = z.object({
  includeInactive: queryBooleanSchema,
});

// ---------------------------------------------------------------------------
// Contract — literal paths before `:param` paths (contract-route-order.test.ts)
// ---------------------------------------------------------------------------

/** Every read: not signed in, not permitted, not found (or not the caller's team's, or module off). */
const read = {
  401: errorBodySchema,
  403: errorBodySchema,
  404: errorBodySchema,
} as const;

/** Every write adds a stale token or an illegal move (409) and a rule the body cannot express (422). */
const write = {
  ...read,
  409: errorBodySchema,
  422: errorBodySchema,
} as const;

export const travelDeskContract = c.router(
  {
    // ---- Queue, history, summary ------------------------------------------------------------

    listQueue: {
      method: 'GET',
      path: '/queue',
      query: listTravelDeskQueueQuerySchema,
      responses: {
        200: paginatedSchema(travelDeskBookingSchema),
        ...read,
      },
      summary:
        "The desk queue: bookings still to do for the caller's teams (or every team's, for a manager), most urgent first",
      metadata: routeMeta('travel.desk:read'),
    },

    getQueueSummary: {
      method: 'GET',
      path: '/queue/summary',
      query: travelDeskQueueSummaryQuerySchema,
      responses: { 200: travelDeskQueueSummarySchema, ...read },
      summary: 'Counts of open, unassigned, breached and due-soon bookings in the same scope',
      metadata: routeMeta('travel.desk:read'),
    },

    getSlaDashboard: {
      method: 'GET',
      path: '/sla',
      query: travelDeskSlaQuerySchema,
      responses: { 200: travelDeskSlaDashboardSchema, ...read },
      summary:
        'Live SLA position per desk team and agent: open, unassigned, breached, due soon, and trips no team covers',
      metadata: routeMeta('travel.desk:manage'),
    },

    listHistory: {
      method: 'GET',
      path: '/history',
      query: listTravelDeskHistoryQuerySchema,
      responses: {
        200: paginatedSchema(travelDeskBookingSchema),
        ...read,
      },
      summary: 'Bookings that have left the queue — confirmed, declined, cancelled or replaced',
      metadata: routeMeta('travel.desk:read'),
    },

    listCredits: {
      method: 'GET',
      path: '/credits',
      query: listTravelDeskCreditsQuerySchema,
      responses: {
        200: paginatedSchema(travelDeskCreditSchema),
        ...read,
      },
      summary:
        'Airline credits travellers hold — filter by traveller and vendor for the credits a booking could use',
      metadata: routeMeta('travel.desk:read'),
    },

    // ---- Teams, members, coverage (config: manage) --------------------------------------------

    listTeams: {
      method: 'GET',
      path: '/teams',
      query: listTravelDeskTeamsQuerySchema,
      responses: { 200: z.array(travelDeskTeamSchema), ...read },
      summary:
        'Desk teams with their members, coverage and workload; a desk agent sees the teams they are on',
      metadata: routeMeta('travel.desk:read'),
    },

    createTeam: {
      method: 'POST',
      path: '/teams',
      body: createTravelDeskTeamBodySchema,
      responses: { 201: travelDeskTeamSchema, ...write },
      summary: 'Create a desk team with its members and the places it covers',
      metadata: routeMeta('travel.desk:manage'),
    },

    // ---- Vendors ----------------------------------------------------------------------------

    listVendors: {
      method: 'GET',
      path: '/vendors',
      query: listTravelDeskVendorsQuerySchema,
      responses: { 200: z.array(travelDeskVendorSchema), ...read },
      summary:
        'Airlines, railways, hotels and agencies the desk books with; inactive ones on request',
      metadata: routeMeta('travel.desk:read'),
    },

    createVendor: {
      method: 'POST',
      path: '/vendors',
      body: createTravelDeskVendorBodySchema,
      responses: { 201: travelDeskVendorSchema, ...write },
      summary: 'Add a vendor',
      metadata: routeMeta('travel.desk:manage'),
    },

    // ---- Shared bookings ----------------------------------------------------------------------

    createSharedBooking: {
      method: 'POST',
      path: '/shared-bookings',
      headers: idempotencyHeadersSchema,
      body: createTravelDeskSharedBookingBodySchema,
      responses: { 201: travelDeskSharedBookingSchema, ...write },
      summary:
        "Confirm several travellers' bookings under one PNR or invoice, each with its own split",
      metadata: routeMeta('travel.desk:book'),
    },

    getSharedBooking: {
      method: 'GET',
      path: '/shared-bookings/:sharedBookingId',
      pathParams: sharedBookingParams,
      responses: { 200: travelDeskSharedBookingSchema, ...read },
      summary: "A group booking with each traveller's share",
      metadata: routeMeta('travel.desk:read'),
    },

    // ---- One trip, one booking ------------------------------------------------------------------

    getDeskTrip: {
      method: 'GET',
      path: '/trips/:tripId',
      pathParams: tripParams,
      responses: { 200: travelDeskTripSchema, ...read },
      summary:
        'A trip as the desk sees it: itinerary, co-travellers and every booking — a trip of a team the caller is not on is a 404',
      metadata: routeMeta('travel.desk:read'),
    },

    getBooking: {
      method: 'GET',
      path: '/bookings/:bookingId',
      pathParams: bookingParams,
      responses: { 200: travelDeskBookingDetailSchema, ...read },
      summary:
        'One booking with what to book, the shared options, the money, the documents and the desk trail',
      metadata: routeMeta('travel.desk:read'),
    },

    // ---- Booking actions (travel.desk:book) -----------------------------------------------------

    claimBooking: {
      method: 'POST',
      path: '/bookings/:bookingId/claim',
      pathParams: bookingParams,
      headers: idempotencyHeadersSchema,
      body: claimTravelDeskBookingBodySchema,
      responses: { 200: travelDeskBookingDetailSchema, ...write },
      summary: 'Take an unassigned booking from the team pool',
      metadata: routeMeta('travel.desk:book'),
    },

    reassignBooking: {
      method: 'POST',
      path: '/bookings/:bookingId/reassign',
      pathParams: bookingParams,
      headers: idempotencyHeadersSchema,
      body: reassignTravelDeskBookingBodySchema,
      responses: { 200: travelDeskBookingDetailSchema, ...write },
      summary: 'Move a booking to another agent, back to the team pool, or to another team',
      metadata: routeMeta('travel.desk:manage'),
    },

    shareOptions: {
      method: 'POST',
      path: '/bookings/:bookingId/options',
      pathParams: bookingParams,
      headers: idempotencyHeadersSchema,
      body: shareTravelDeskOptionsBodySchema,
      responses: { 200: travelDeskBookingDetailSchema, ...write },
      summary: 'Share options with the traveller to choose from; the booking waits for their pick',
      metadata: routeMeta('travel.desk:book'),
    },

    confirmBooking: {
      method: 'POST',
      path: '/bookings/:bookingId/confirm',
      pathParams: bookingParams,
      headers: idempotencyHeadersSchema,
      body: confirmTravelDeskBookingBodySchema,
      responses: { 200: travelDeskBookingDetailSchema, ...write },
      summary:
        'Record a booking as confirmed with its vendor, reference, amount and GST; over the tolerance it goes on hold for the manager instead',
      metadata: routeMeta('travel.desk:book'),
    },

    declineBooking: {
      method: 'POST',
      path: '/bookings/:bookingId/decline',
      pathParams: bookingParams,
      headers: idempotencyHeadersSchema,
      body: declineTravelDeskBookingBodySchema,
      responses: { 200: travelDeskBookingDetailSchema, ...write },
      summary:
        'Say this item cannot be booked, with a reason; the traveller decides what to do next',
      metadata: routeMeta('travel.desk:book'),
    },

    cancelBooking: {
      method: 'POST',
      path: '/bookings/:bookingId/cancel',
      pathParams: bookingParams,
      headers: idempotencyHeadersSchema,
      body: cancelTravelDeskBookingBodySchema,
      responses: { 200: travelDeskBookingDetailSchema, ...write },
      summary:
        'Cancel a booking after an approved change or cancellation, recording the charge, who bears it and any refund due',
      metadata: routeMeta('travel.desk:book'),
    },

    recordRefund: {
      method: 'POST',
      path: '/bookings/:bookingId/refund-received',
      pathParams: bookingParams,
      headers: idempotencyHeadersSchema,
      body: recordTravelDeskRefundBodySchema,
      responses: { 200: travelDeskBookingDetailSchema, ...write },
      summary:
        'Record that the refund arrived — to the company, the employee, or as an airline credit note',
      metadata: routeMeta('travel.desk:book'),
    },

    forfeitRefund: {
      method: 'POST',
      path: '/bookings/:bookingId/refund-forfeited',
      pathParams: bookingParams,
      headers: idempotencyHeadersSchema,
      body: forfeitTravelDeskRefundBodySchema,
      responses: { 200: travelDeskBookingDetailSchema, ...write },
      summary: 'Give up on a refund that will not come, with a reason',
      metadata: routeMeta('travel.desk:manage'),
    },

    addBookingComment: {
      method: 'POST',
      path: '/bookings/:bookingId/comments',
      pathParams: bookingParams,
      headers: idempotencyHeadersSchema,
      body: addTravelDeskBookingCommentBodySchema,
      responses: { 201: travelDeskBookingDetailSchema, ...write },
      summary: 'Add an internal note to a booking; the traveller never sees it',
      metadata: routeMeta('travel.desk:book'),
    },

    // ---- Documents (the desk owns upload; the traveller downloads via the trip, a later `travel-request.contract.ts` route) -------

    uploadDocument: {
      method: 'POST',
      path: '/trips/:tripId/documents',
      pathParams: tripParams,
      headers: idempotencyHeadersSchema,
      body: uploadTravelDeskDocumentBodySchema,
      responses: {
        201: travelDeskDocumentSchema,
        ...write,
        /** Upload rate limit (D34). */
        429: errorBodySchema,
      },
      summary: 'Attach a ticket, voucher, invoice or note to a trip or one of its bookings',
      // ai-hidden because the REQUEST is unsatisfiable by a model: the body carries
      // `contentBase64` (see `contract-tools.file-upload.test.ts`).
      metadata: routeMeta('travel.desk:book', { ai: 'hidden' }),
    },

    downloadDocument: {
      method: 'GET',
      path: '/documents/:documentId',
      pathParams: documentParams,
      responses: { 200: travelDeskDocumentDownloadSchema, ...read },
      summary: 'Download one of the desk documents; access is re-checked on every fetch',
      metadata: routeMeta('travel.desk:read', { ai: 'hidden' }),
    },

    removeDocument: {
      method: 'DELETE',
      path: '/documents/:documentId',
      pathParams: documentParams,
      headers: idempotencyHeadersSchema,
      body: c.noBody(),
      responses: { 200: z.object({ id: uuidSchema }), ...write },
      summary: 'Remove a document attached by mistake; the removal stays on the booking trail',
      metadata: routeMeta('travel.desk:book'),
    },

    // ---- Traveller reveal (PII) -------------------------------------------------------------------

    revealTraveller: {
      method: 'POST',
      path: '/trips/:tripId/traveller/reveal',
      pathParams: tripParams,
      body: revealTravelDeskTravellerBodySchema,
      responses: {
        200: travelDeskTravellerRevealSchema,
        ...write,
        /** Reveal rate limit (D34). */
        429: errorBodySchema,
      },
      summary:
        "Decrypt the traveller's passport and visa numbers for a booking the caller's team holds on an open trip; audited every time",
      metadata: routeMeta('travel.desk:book', { ai: 'hidden' }),
    },

    // ---- Teams, members, coverage: one team --------------------------------------------------------

    updateTeam: {
      method: 'PATCH',
      path: '/teams/:teamId',
      pathParams: teamParams,
      body: updateTravelDeskTeamBodySchema,
      responses: { 200: travelDeskTeamSchema, ...write },
      summary:
        "Change a team's name, mode, lead, members or coverage; the member and coverage lists replace the old ones",
      metadata: routeMeta('travel.desk:manage'),
    },

    // ---- Vendors: one vendor -------------------------------------------------------------------------

    updateVendor: {
      method: 'PATCH',
      path: '/vendors/:vendorId',
      pathParams: vendorParams,
      body: updateTravelDeskVendorBodySchema,
      responses: { 200: travelDeskVendorSchema, ...write },
      summary: 'Edit a vendor, or deactivate it; its existing bookings keep it',
      metadata: routeMeta('travel.desk:manage'),
    },
  },
  { pathPrefix: '/travel/desk' },
);
